import { Toast as ToastPR, ToastProps } from "primereact/toast";
import React, { useRef } from "react";

export type ToastSeverity = "success" | "info" | "warn" | "error" | "secondary" | "contrast";

export interface IToastProps extends ToastProps {
  className?: string;
  style?: object;
  variant?: "primary" | "secondary";
  disabled?: boolean;
  validation?: string;
  errorType?: string;
  message?: string;
  heading?: string;
  severity?: ToastSeverity;
  sticky?: boolean;
  life?: number;
  appendTo?: "self" | HTMLElement | (() => HTMLElement) | null;
}

export interface ToastConfig {
  message?: string;
  heading?: string;
  severity?: ToastSeverity;
  sticky?: boolean;
  life?: number;
}

export interface IToastRef {
  showFunction: (config?: ToastConfig) => void;
  show: ToastPR["show"];
  clear: ToastPR["clear"];
  replace: ToastPR["replace"];
  remove: ToastPR["remove"];
}

interface SeverityAppearance {
  container: string;
  heading: string;
  message: string;
  button?: string;
  iconWrapper: string;
  icon: string;
}

const getSeverityAppearance = (severity: ToastSeverity = "info"): SeverityAppearance => {
  switch (severity) {
    case "error":
      return {
        container: "bg-red-900/95 text-red-50 shadow-lg border border-red-600/60",
        heading: "text-red-50",
        message: "text-red-100",
        button:
          "text-red-50 bg-red-500/15 hover:bg-red-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-200/60",
        iconWrapper: "bg-red-500/20 text-red-100 border border-red-400/60 text-xs h-8 w-8",
        icon: "pi pi-times text-sm",
      };
    case "success":
      return {
        container: "bg-green-900/95 text-green-50 shadow-lg border border-green-600/60",
        heading: "text-green-50",
        message: "text-green-100",
        button:
          "text-green-50 bg-green-500/15 hover:bg-green-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-200/60",
        iconWrapper: "bg-green-500/20 text-green-100 border border-green-400/60 text-xs h-8 w-8",
        icon: "pi pi-check text-sm",
      };
    case "warn":
      return {
        container: "bg-yellow-100 text-yellow-900 shadow-lg border border-yellow-400/80",
        heading: "text-yellow-900",
        message: "text-yellow-800",
        button:
          "text-yellow-900 bg-yellow-500/15 hover:bg-yellow-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300/60",
        iconWrapper: "bg-yellow-500/20 text-yellow-900 border border-yellow-500/60 text-xs h-8 w-8",
        icon: "pi pi-exclamation-circle text-sm",
      };
    case "secondary":
    case "contrast":
    case "info":
    default:
      return {
        container: "bg-blue-900/95 text-blue-50 shadow-lg border border-blue-600/60",
        heading: "text-blue-50",
        message: "text-blue-100",
        button:
          "text-blue-50 bg-blue-500/15 hover:bg-blue-500/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-200/60",
        iconWrapper: "bg-blue-500/20 text-blue-100 border border-blue-400/60 text-xs h-8 w-8",
        icon: "pi pi-info-circle text-sm",
      };
  }
};

const Toast = React.forwardRef<IToastRef, IToastProps>((props, ref) => {
  const toastRef = useRef<ToastPR>(null);
  const getContentClassName = (severity?: ToastSeverity) => {
    const variant = props.variant || "primary";
    const isDisabled = props.disabled;
    const hasError = props.validation && props.errorType === "BORDER";
    const { container } = getSeverityAppearance(severity ?? props.severity ?? "info");

    // Base styles
    const base = `
      outline-none
      text-sm
      ${variant === "primary" ? "p-0 h-auto w-full" : "p-0 h-auto w-full"}
      text-left
      ${variant === "secondary" ? " shadow-toast" : ""}
    `
      .trim()
      .replace(/\s+/g, " ");

    let classes = `${base} ${container}`;

    // Handle disabled state
    if (isDisabled) {
      classes = `${classes} opacity-50 cursor-not-allowed`;
    }

    // Handle error state
    if (hasError) {
      classes = `${classes} border-border-error`;
    }

    // Handle custom className override
    if (props.className) {
      classes = `${classes} ${props.className}`;
    }

    return classes.replace(/\s+/g, " ");
  };

  React.useImperativeHandle(ref, () => ({
    showFunction(config) {
      const mergedConfig = {
        message: config?.message ?? props.message,
        heading: config?.heading ?? props.heading,
        severity: config?.severity ?? props.severity ?? "info",
        sticky: config?.sticky ?? props.sticky ?? false,
        life: config?.life ?? props.life,
      };

      if (toastRef.current && mergedConfig.message) {
        const appearance = getSeverityAppearance(mergedConfig.severity);

        toastRef.current.show({
          severity: mergedConfig.severity,
          closable: false,
          life: mergedConfig.sticky ? undefined : mergedConfig.life,
          content: (
            <div className={`my-auto flex w-full items-center justify-between p-4`}>
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-10 w-10 items-center justify-center ${appearance.iconWrapper}`}
                >
                  <i className={appearance.icon} />
                </div>
                <div className="flex flex-col">
                  {mergedConfig.heading && (
                    <div className={`text-xs font-bold tracking-normal ${appearance.heading}`}>
                      {mergedConfig.heading}
                    </div>
                  )}
                  <div className={`text-xs font-light tracking-normal ${appearance.message}`}>
                    {mergedConfig.message}
                  </div>
                </div>
              </div>
              <button
                type="button"
                className={`ml-3 flex h-8 w-8 items-center justify-center rounded-full transition ${
                  appearance.button ?? ""
                }`}
                onClick={() => {
                  if (toastRef.current) {
                    toastRef.current.clear();
                  }
                }}
              >
                <i className="pi pi-times text-base" />
              </button>
            </div>
          ),
          contentClassName: getContentClassName(mergedConfig.severity),
          sticky: mergedConfig.sticky,
        });
      }
    },
    show: (...args) => toastRef.current?.show(...args),
    clear: () => toastRef.current?.clear(),
    replace: (...args) => toastRef.current?.replace(...args),
    remove: (...args) => toastRef.current?.remove(...args),
  }));

  const { position, appendTo } = props;

  // Default to document.body to avoid overflow issues, but allow override
  const getAppendTo = () => {
    if (appendTo === "self") return "self";
    if (appendTo === null || appendTo === undefined) return document.body;
    if (typeof appendTo === "function") return appendTo();
    return appendTo;
  };

  return <ToastPR position={position} ref={toastRef} appendTo={getAppendTo()} />;
});

export default React.memo(Toast);
